import {
  cancelSubscription,
  createEvoClient,
  getCreditCard,
  pauseSubscription,
  resumeSubscription,
  showUserSubscription,
  subscriptionConfigurations,
  updateSubscription,
} from "@jti/evo-client";
import { getConfig, keysToCamelCase, keysToSnakeCase } from "@jti/fe-common";
import type { MagentoCurrencyCode, MagentoPrice } from "@jti/magento-types";
import type {
  CreditCard,
  CreditCardApiResponse,
  GetUserSubscriptionFormattedResponse,
  GetUserSubscriptionResponse,
  SubscriptionsConfiguration,
  UpdateUserSubscriptionPayload,
  UserSubscription,
  UserSubscriptionMutationApiResponse,
  UserSubscriptionMutationResponse,
} from "@jti/rest-api-types";

import { getUser } from "../../utils";

type SubscriptionPrice = { amount: MagentoPrice["value"]; currency: MagentoCurrencyCode };

export type CancelUserSubscriptionPayload = {
  reasonDescription: string;
};

export class SubscriptionsApi {
  readonly name = "SubscriptionApi";
  private readonly client;

  constructor(baseUrl: string, tenantToken: string, recaptchaId: string) {
    const { ciam_token } = getUser();
    this.client = createEvoClient({
      baseUrl,
      tenantToken,
      recaptchaId,
      ciamToken: ciam_token,
      throwOnError: true,
    });
  }

  static init(): SubscriptionsApi {
    const { EVO_DOMAIN, EVO_TENANT, "X-RECAPTCHA-ID": recaptchaId } = getConfig();
    return new SubscriptionsApi(EVO_DOMAIN, EVO_TENANT, recaptchaId);
  }

  formatResponseWithPrice<T>(response: T & SubscriptionPrice) {
    const { amount, currency, ...rest } = response;

    const price: MagentoPrice = {
      value: amount,
      currency,
    };

    return {
      ...rest,
      price,
    };
  }

  async getSubscriptionConfigurations(): Promise<SubscriptionsConfiguration> {
    const { data } = await subscriptionConfigurations({ client: this.client, throwOnError: true });

    return keysToCamelCase(data);
  }

  async getUserSubscription(): Promise<UserSubscription> {
    const { data } = await showUserSubscription({ client: this.client });

    const response = data as GetUserSubscriptionResponse;

    const productsWithEnrichedParameters = response.products.map((product) => {
      const optionIDs = product.options?.reduce((acc: string, { sku, qty }) => `${acc}_${sku}_${qty}_`, "") ?? [];

      return {
        _id: `${product.sku}_${optionIDs}`,
        ...product,
      };
    });

    const formattedResponse = this.formatResponseWithPrice({
      ...response,
      products: productsWithEnrichedParameters,
    });

    const keysToCamelCaseResponse = keysToCamelCase<GetUserSubscriptionFormattedResponse>(formattedResponse);

    return keysToCamelCaseResponse;
  }

  async updateSubscription(
    uuid: string,
    payload: UpdateUserSubscriptionPayload
  ): Promise<UserSubscriptionMutationResponse> {
    const preparedProducts = payload.products?.map(({ _id, ...rest }) => {
      return rest;
    });

    const requestPayload = {
      ...payload,
      products: preparedProducts,
    };

    const requestBody = keysToSnakeCase(requestPayload);

    const { data } = await updateSubscription({
      client: this.client,
      path: { uuid },
      body: requestBody,
    });

    const response = data as UserSubscriptionMutationApiResponse;
    const formattedResponse = this.formatResponseWithPrice(response);

    return keysToCamelCase(formattedResponse);
  }

  async pauseUserSubscription(uuid: string): Promise<UserSubscriptionMutationResponse> {
    const { data } = await pauseSubscription({
      client: this.client,
      path: { uuid },
    });

    const response = data as UserSubscriptionMutationApiResponse;
    const formattedResponse = this.formatResponseWithPrice(response);

    return keysToCamelCase(formattedResponse);
  }

  async cancelUserSubscription(
    uuid: string,
    payload?: CancelUserSubscriptionPayload
  ): Promise<UserSubscriptionMutationResponse> {
    const body = payload ? { reason_description: payload.reasonDescription } : undefined;

    const { data } = await cancelSubscription({
      client: this.client,
      path: { uuid },
      body,
    });

    const response = data as UserSubscriptionMutationApiResponse;
    return keysToCamelCase(this.formatResponseWithPrice(response));
  }

  async resumeUserSubscription(uuid: string): Promise<UserSubscriptionMutationResponse> {
    const { data } = await resumeSubscription({
      client: this.client,
      path: { uuid },
    });

    const response = data as UserSubscriptionMutationApiResponse;
    return keysToCamelCase(this.formatResponseWithPrice(response));
  }

  async getCardData(uuid: string): Promise<CreditCard> {
    const { data } = await getCreditCard({
      client: this.client,
      path: { uuid },
    });

    const response = data as CreditCardApiResponse;
    return keysToCamelCase(response);
  }
}
